import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ProductGalleryControls.css';

const FALLBACK_IMAGE = '/assets/img/logo-no-image.png';

function normalizeImages(galleryList) {
  if (Array.isArray(galleryList)) {
    return galleryList.filter(Boolean);
  }

  if (typeof galleryList === 'string') {
    return galleryList
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function ProductGallery({ galleryList, activeImageUrl = '' }) {
  const images = useMemo(() => normalizeImages(galleryList), [galleryList]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const thumbsRef = useRef(null);

  useEffect(() => {
    const track = thumbsRef.current;
    const selected = track && track.children[activeIndex];
    if (!selected) return;
    const left = selected.offsetLeft - track.offsetLeft - (track.clientWidth - selected.offsetWidth) / 2;
    track.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }, [activeIndex, images]);

  useEffect(() => {
    const nextIndex = activeImageUrl ? images.findIndex((image) => image === activeImageUrl) : -1;
    setActiveIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [images, activeImageUrl]);

  useEffect(() => {
    if (!isPreviewOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsPreviewOpen(false);
      }

      if (event.key === 'ArrowLeft') {
        setActiveIndex((currentIndex) => (currentIndex === 0 ? images.length - 1 : currentIndex - 1));
      }

      if (event.key === 'ArrowRight') {
        setActiveIndex((currentIndex) => (currentIndex + 1) % images.length);
      }
    };

    document.body.classList.add('product-gallery-preview-open');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('product-gallery-preview-open');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [images.length, isPreviewOpen]);

  if (!images.length) {
    return (
      <div className="product-gallery product-gallery--empty">
        <img
          className="product-gallery__empty-image"
          src={FALLBACK_IMAGE}
          alt="Product preview unavailable"
        />
      </div>
    );
  }

  const activeImage = images[Math.min(activeIndex, images.length - 1)] || FALLBACK_IMAGE;
  const showSliderControls = images.length > 1;
  const goToPreviousImage = () => setActiveIndex((currentIndex) => (currentIndex === 0 ? images.length - 1 : currentIndex - 1));
  const goToNextImage = () => setActiveIndex((currentIndex) => (currentIndex + 1) % images.length);

  return (
    <div className="product-gallery">
      <button
        type="button"
        className="product-gallery__hero"
        onClick={() => setIsPreviewOpen(true)}
        aria-label="Open product image preview"
      >
        <img
          src={activeImage}
          alt="Product preview"
          className="product-gallery__hero-image"
          onError={(event) => {
            event.target.src = FALLBACK_IMAGE;
          }}
        />
      </button>

      {images.length > 1 ? (
        <div className="product-gallery__thumbnail-nav">
          <button type="button" className="product-gallery__thumbnail-arrow" onClick={goToPreviousImage} aria-label="Previous product image">
            <i className="fa fa-angle-left" aria-hidden="true" />
          </button>
        <div className="product-gallery__thumbs" ref={thumbsRef} aria-label="Product image thumbnails">
          {images.map((image, index) => {
            const isActive = index === activeIndex;

            return (
              <button
                key={`${image}-${index}`}
                type="button"
                className={`product-gallery__thumb ${isActive ? 'product-gallery__thumb--active' : ''}`}
                onClick={() => setActiveIndex(index)}
                aria-label={`View product image ${index + 1}`}
                aria-pressed={isActive}
              >
                <img
                  src={image}
                  alt={`Product thumbnail ${index + 1}`}
                  className="product-gallery__thumb-image"
                  loading="lazy"
                  onError={(event) => {
                    event.target.src = FALLBACK_IMAGE;
                  }}
                />
              </button>
            );
          })}
        </div>
          <button type="button" className="product-gallery__thumbnail-arrow" onClick={goToNextImage} aria-label="Next product image">
            <i className="fa fa-angle-right" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {isPreviewOpen ? (
        <div
          className="product-gallery-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Product image slider"
        >
          <button
            type="button"
            className="product-gallery-modal__backdrop"
            onClick={() => setIsPreviewOpen(false)}
            aria-label="Close product image preview"
          />
          <div className="product-gallery-modal__content">
            <button
              type="button"
              className="product-gallery-modal__close"
              onClick={() => setIsPreviewOpen(false)}
              aria-label="Close product image preview"
            >
              ×
            </button>

            {showSliderControls ? (
              <button
                type="button"
                className="product-gallery-modal__arrow product-gallery-modal__arrow--prev"
                onClick={goToPreviousImage}
                aria-label="Previous product image"
              >
                ‹
              </button>
            ) : null}

            <div className="product-gallery-modal__stage">
              <img
                src={activeImage}
                alt={`Product preview ${activeIndex + 1}`}
                className="product-gallery-modal__image"
                onError={(event) => {
                  event.target.src = FALLBACK_IMAGE;
                }}
              />
            </div>

            {showSliderControls ? (
              <button
                type="button"
                className="product-gallery-modal__arrow product-gallery-modal__arrow--next"
                onClick={goToNextImage}
                aria-label="Next product image"
              >
                ›
              </button>
            ) : null}

            {showSliderControls ? (
              <div className="product-gallery-modal__thumbs" aria-label="Preview image thumbnails">
                {images.map((image, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <button
                      key={`${image}-modal-${index}`}
                      type="button"
                      className={`product-gallery-modal__thumb ${isActive ? 'product-gallery-modal__thumb--active' : ''}`}
                      onClick={() => setActiveIndex(index)}
                      aria-label={`Show preview image ${index + 1}`}
                      aria-pressed={isActive}
                    >
                      <img
                        src={image}
                        alt={`Product preview thumbnail ${index + 1}`}
                        className="product-gallery-modal__thumb-image"
                        loading="lazy"
                        onError={(event) => {
                          event.target.src = FALLBACK_IMAGE;
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProductGallery;
